/**
 * Cohort detection utilities for Plan Selector
 * Determines which UX variant to show based on user's plan and pricing structure
 */

import type { ObPlanId } from '../../../gql/graphql'
import { OBPlanIdValues } from '../types'

/**
 * Plan Selector cohort types
 * Determines which UX variant to display based on user's plan and pricing structure
 */
export enum PlanSelectorCohort {
  /** Tiered pricing with volume discounts (3+ pricing tiers) */
  Tiered = 'tiered',
  /** Non-tiered flat pricing (1 pricing tier) */
  NonTiered = 'non-tiered',
  /** Currently on Agency plan - shows Agency as selectable option */
  Agency = 'agency',
}

interface SubscriptionPlan {
  id: ObPlanId
  pricing?: Array<{ upperBound: number }>
}

interface Subscription {
  plan: SubscriptionPlan
}

interface Plan {
  pricing: Array<{ upperBound: number }>
}

/**
 * Detects which pricing cohort the user belongs to
 *
 * Based on VLG-384 pattern: sortedPricingTiers.length > 2 indicates tiered pricing
 *
 * Cohorts:
 * - tiered: Volume discounts (3+ pricing tiers), shows Free/Essentials/Team
 * - non-tiered: Flat pricing (1 pricing tier), shows Free/Essentials/Team (non-tiered)
 * - agency: Currently on Agency plan, shows Essentials/Team/Agency cards
 */
export function detectPlanSelectorCohort(
  subscription?: Subscription | null,
  availablePlans?: Array<Plan> | null,
): PlanSelectorCohort {
  // Agency users are special cohort (see Agency plan card instead of CTA)
  if (subscription?.plan?.id === OBPlanIdValues.agency) {
    return PlanSelectorCohort.Agency
  }

  // Tiered pricing has >2 pricing tiers (volume discounts)
  // Non-tiered has 1-2 tiers (flat rate or Agency structure)
  const hasVolumePricing = availablePlans?.some(
    (plan) => plan.pricing.length > 2,
  )

  return hasVolumePricing
    ? PlanSelectorCohort.Tiered
    : PlanSelectorCohort.NonTiered
}
